import { Injectable, Inject, Optional } from "@angular/core";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { map, take, takeUntil, tap } from "rxjs/operators";
import {
  LoadPreviousScores,
  ScoreItem,
  ResponseTransportOfUserSettingItem,
} from "./score.model";
import { CategoryDto, LastScoreResponse, ScoreService, API_BASE_URL } from "src/swagger/api.client.generated";
import { SettingsLocalService } from "../settings/settings.service";
import { ScoreType } from "@app/common/dialog-add-score/dialog-add-score.model";
import * as moment from "moment";
import { HttpClient } from "@angular/common/http";
import { APP_DAY_BOUNDARY_MINUTES } from "@app/core/helpers/date.helper";
import { UserService } from "@app/authentication/user.service";

// Temporary interface until swagger client is regenerated
// Note: Property names match C# model (LastScore, ScoresCountToday) as JSON serialization preserves them
export interface LastScoreWithCountResponse {
  lastScore: LastScoreResponse | null;
  scoresCountToday: number;
}

@Injectable({
  providedIn: "root",
})
export class ScoreLocalService {
  private haloSource = new Subject<ScoreItem>();
  private zombieSource = new Subject<ScoreItem>();
  private categoriesSource = new BehaviorSubject<CategoryDto[]>([]);
  private cancelCategoriesFetch$ = new Subject<void>();
  private scoreDialogRequestedSource = new Subject<ScoreItem>();
  private scoreCacheUpdatedSource = new Subject<void>();
  /** true = a draft write just failed, false = a previously-failed write just succeeded on retry. */
  private draftWriteStatusSource = new Subject<boolean>();

  halosChanged = this.haloSource.asObservable();
  zombiesChanged = this.zombieSource.asObservable();
  categoriesChanged = this.categoriesSource.asObservable();
  scoreDialogRequested = this.scoreDialogRequestedSource.asObservable();
  scoreCacheUpdated = this.scoreCacheUpdatedSource.asObservable();
  draftWriteStatusChanged = this.draftWriteStatusSource.asObservable();

  private zombies: ScoreItem[] = [];
  private halos: ScoreItem[] = [];
  private updateButtonFlag: boolean;
  private hasAcknowledgedEmptyShadowsPrompt: boolean = false;

  private newScoreSession: boolean = true;
  private preLoadHalos: boolean = true;
  private preLoadZombies: boolean = true;
  private cachedScore: LastScoreResponse | null = null;
  private cachedScoreIsWithin6Hours: boolean | null = null;
  private cachedScoreDate: moment.Moment | null = null;
  private cachedScoresCountToday: number | null = null;
  private lastDraftWriteFailed = false;

  constructor(
    private scoreService: ScoreService,
    private settingsLocalService: SettingsLocalService,
    private http: HttpClient,
    private userService: UserService,
    @Optional() @Inject(API_BASE_URL) private baseUrl?: string
  ) {
    // getCategories() is intentionally NOT called here. It's triggered by app.component
    // once a valid session with a confirmed active subscription exists, so constructing
    // this root singleton (e.g. via the nav) no longer fires an authenticated API call
    // for an unauthenticated/unconfirmed user.
    //
    // Pure local-storage bookkeeping, no API call, so it is safe at construction —
    // and construction is exactly once per page load, which is the lifetime we want.
    this.expireStaleScoreSessionFlags();
  }

  /**
   * Ends a score session that began before the current app day.
   *
   * Submitting a score used to be the only thing that ended a session
   * (clearHalosAndZombies() runs only from completeScoreNow()), so a session
   * abandoned rather than submitted kept its recorded reuse decisions forever.
   * Those stale decisions suppressed the "use previous stars/shadows?" prompts
   * and, via the empty-Shadows warning, could let Score Now submit with no
   * Shadows at all — on every subsequent day.
   *
   * Uses the same app-day boundary (3:59am) as the last-score cache, and runs once per
   * page load rather than on a timer, so it can never wipe state out from under
   * a session the user is actively working in.
   *
   * Deliberately leaves `halos`/`zombies` in place: expiring a stale decision
   * must not destroy scores the user typed but has not submitted. The
   * consequence is that yesterday's leftover items can still be present — but
   * the reuse prompt now fires and offers to clear them, which is the point.
   */
  private expireStaleScoreSessionFlags(): void {
    // Dead key: the empty-Shadows acknowledgement is held in memory now. Dropped
    // on load so it cannot linger in storage and mislead anyone reading it.
    localStorage.removeItem("hasAcknowledgedEmptyShadowsPrompt");

    // An older build set newScoreSession=false when the user switched to the
    // Shadows tab — the very bug that pinned showPrompt off for the rest of the
    // session. No current code path writes false, so a stored false can only be
    // legacy state, and it would never correct itself: setNewScoreSession()
    // adopts a stored value unless forced, and the stamp check below takes the
    // "no stamp yet" branch for these users and returns without clearing. The
    // user would stay unable to see the prompt until they happened to submit a
    // score or cross a day boundary. Treat it as an expired session instead.
    if (JSON.parse(localStorage.getItem("newScoreSession")) === false) {
      this.endScoreSession();
      return;
    }

    const startedDay = localStorage.getItem("scoreSessionDay");
    if (startedDay == null) {
      // Either the first ever session, or one already in progress from a build
      // that predates this stamp. Adopt today so it expires on the next boundary
      // instead of being treated as infinitely stale.
      this.stampScoreSessionDay();
      return;
    }

    if (
      moment(startedDay, "YYYY-MM-DD HH:mm:ss").isSameOrAfter(
        this.getCurrentDayBoundary()
      )
    ) {
      return;
    }

    this.endScoreSession();
  }

  /**
   * Drops the per-session reuse decisions and starts a fresh session.
   *
   * Per-session state only — `halos`/`zombies` are deliberately untouched, so
   * ending a session never destroys scores the user typed but has not submitted.
   * The LoadPrevious* keys are a cache of the preference rather than the
   * preference itself (the DB is authoritative), so dropping them just forces a
   * re-read.
   */
  private endScoreSession(): void {
    localStorage.removeItem("halosReuseDecisionMade");
    localStorage.removeItem("zombiesReuseDecisionMade");
    localStorage.removeItem("LoadPreviousHalos");
    localStorage.removeItem("LoadPreviousZombies");
    // Re-stamps the day as a side effect of writing newScoreSession.
    this.setNewScoreSession(true, true);
  }

  /** Records the app day the current score session began. */
  private stampScoreSessionDay(): void {
    localStorage.setItem(
      "scoreSessionDay",
      moment().format("YYYY-MM-DD HH:mm:ss")
    );
  }

  /** The "YYYY-MM-DD HH:mm:ss" stamp of when the current draft's session began, for the recovery
   *  modal's "from [time]" / "from [date] at [time]" copy (FLIP-302). */
  getScoreSessionDay(): string | null {
    return localStorage.getItem("scoreSessionDay");
  }

  /**
   * Gets the last score with caching to minimize API calls
   * Caching strategy:
   * - Returns cached score if it exists and is within 6 hours
   * - Returns cached result (even if null) if cache was recently updated
   * - Otherwise fetches from API and updates cache
   * @returns Observable of the last score or null if no score exists
   */
  getLastScore(): Observable<LastScoreResponse> {
    // A cache captured before the most recent day boundary (3:59am) belongs to a
    // previous day — e.g. a long-lived session left open overnight — and must not
    // be trusted for "did I score / how many times today" decisions.
    if (this.cachedScoreDate !== null && this.cachedScoreDate.isBefore(this.getCurrentDayBoundary())) {
      this.cachedScore = null;
      this.cachedScoreIsWithin6Hours = null;
      this.cachedScoreDate = null;
      this.cachedScoresCountToday = null;
    }

    // Check if cached score exists and is still valid (within 6 hours)
    const isWithin6Hours = this.isScoreWithinTimePeriod(this.cachedScore, 6);
    if (this.cachedScore !== null && isWithin6Hours && this.cachedScoreDate !== null) {
      return of(this.cachedScore);
    }

    // If we've recently checked (cacheDate is set), return cached result (even if null)
    // This prevents excessive API calls when no score exists or score is expired
    if (this.cachedScoreDate !== null) {
        return of(this.cachedScore);
    }

    // If not cached or cache is stale, call the API endpoint that returns both score and count
    return this.getLastScoreWithCount().pipe(
      map((response: LastScoreWithCountResponse) => {
        // Store the score and count in cache
        this.cachedScore = response.lastScore;
        this.cachedScoresCountToday = response.scoresCountToday;
        this.cachedScoreDate = moment();
        // Calculate and store whether score is within 6 hours
        if (response.lastScore) {
          this.cachedScoreIsWithin6Hours = this.isScoreWithinTimePeriod(response.lastScore, 6);
        } else {
          this.cachedScoreIsWithin6Hours = false;
        }
        // Notify subscribers that the cache has been updated (used by other components)
        this.scoreCacheUpdatedSource.next();
        return response.lastScore;
      })
    );
  }

  /**
   * Gets the last score with count of scores for today
   * Uses the new endpoint that returns both score and count
   * @returns Observable of LastScoreWithCountResponse
   */
  private getLastScoreWithCount(): Observable<LastScoreWithCountResponse> {
    const apiBaseUrl = this.baseUrl || "https://localhost:5001";
    const url = `${apiBaseUrl}/api/Score/GetLastScoreWithCount?IsAcceptButton=false&LocalDateTime=${encodeURIComponent(moment().format('YYYY-MM-DD HH:mm:ss'))}`;
    
    return this.http.get<LastScoreWithCountResponse>(url);
  }

  /**
   * The most recent app-day boundary (3:59am local) — scores logged before it belong to a
   * previous day and should be treated as "no score yet" today. Built from
   * APP_DAY_BOUNDARY_MINUTES so the reset time is stated once per stack rather than repeated
   * as a literal here (FLIP-218).
   */
  private getCurrentDayBoundary(): moment.Moment {
    const boundary = moment().startOf('day').add(APP_DAY_BOUNDARY_MINUTES, 'minutes');
    if (moment().isBefore(boundary)) {
      boundary.subtract(1, 'day');
    }
    return boundary;
  }

  /**
   * Gets today's score only — null if no score has been logged since the
   * last 3:59am reset (even if an older score exists from a previous day).
   * @returns Observable of today's score, or null if awaiting a check-in
   */
  getTodaysScore(): Observable<LastScoreResponse | null> {
    return this.getLastScore().pipe(
      map((score) => {
        if (!score || !score.insertLocalDate) {
          return null;
        }
        return moment(score.insertLocalDate).isSameOrAfter(this.getCurrentDayBoundary())
          ? score
          : null;
      })
    );
  }

  getCachedScoreIsWithin6Hours(): boolean | null {
    return this.cachedScoreIsWithin6Hours;
  }

  /**
   * Checks whether the given score was logged since the last app-day boundary (3:59am)
   * (i.e. "today", using the same reset timing as getTodaysScore()).
   */
  isScoreFromToday(score: LastScoreResponse | null): boolean {
    if (!score?.insertLocalDate) {
      return false;
    }
    return moment(score.insertLocalDate).isSameOrAfter(this.getCurrentDayBoundary());
  }

  getCachedScoresCountToday(): number | null {
    return this.cachedScoresCountToday;
  }

  /**
   * Gets the cached score without triggering an API call
   * @returns The cached score or null if not cached
   */
  getCachedScore(): LastScoreResponse | null {
    return this.cachedScore;
  }

  /**
   * Checks if we should ask the user to score again
   * Rules:
   * - If no score exists, ask to score
   * - If score is within 6 hours, don't ask
   * - If score is older than 6 hours, ask to score
   * - If 2 scores already done today, don't ask
   * @returns True if we should ask the user to score
   */
  shouldAskToScore(): boolean {
    // If no cached score, we should ask
    if (!this.cachedScore || !this.cachedScoreDate) {
      return true;
    }

    // If 2 scores already done today, don't ask
    if (this.cachedScoresCountToday !== null && this.cachedScoresCountToday >= 2) {
      return false;
    }

    // Check if score is within 6 hours
    const isWithin6Hours = this.isScoreWithinTimePeriod(this.cachedScore, 6);
    if (isWithin6Hours) {
      return false;
    }

    // Score is older than 6 hours, ask to score
    return true;
  }

  /**
   * Checks if there's an active score (after 4am today and within 6 hours)
   * @returns True if there's an active score, false otherwise
   */
  isScoreActive(): boolean {
    // An active score means: score exists and is within 6 hours
    // The "after 4am today" check is handled by the backend API
    return this.cachedScore !== null && this.cachedScoreIsWithin6Hours === true;
  }

  /**
   * Determines if we should show the margin of safety page (vs. the scoring page)
   * Used for navigation routing decisions. This is the inverse of shouldAskToScore(),
   * except when 2 scores are done today (both return false for shouldAskToScore,
   * but this returns true to show the margin of safety page).
   * 
   * Rules:
   * - If 2 scores already done today, show margin of safety
   * - If score exists and is within 6 hours, show margin of safety
   * - Otherwise, show scoring page
   * 
   * @returns True if we should navigate to margin of safety page, false to go to scoring page
   */
  shouldShowMarginOfSafety(): boolean {
    // If 2 scores already done today, show margin of safety - but only if a real score
    // backs that count. Guards against ScoresCountToday and LastScore ever being computed
    // against different day boundaries (FLIP-153: was routing to the buffer results page
    // with no matching score, rendering blank instead of falling back to the CTA screen).
    if (this.cachedScoresCountToday !== null && this.cachedScoresCountToday >= 2 && this.cachedScore) {
      return true;
    }

    // If no cached score, go to scoring page
    if (!this.cachedScore || !this.cachedScoreDate) {
      return false;
    }

    // Check if score is within 6 hours - if so, show margin of safety
    const isWithin6Hours = this.isScoreWithinTimePeriod(this.cachedScore, 6);
    return isWithin6Hours;
  }

  /**
   * Checks if any score exists (even if expired/older than 6 hours)
   * @returns True if any score exists, false otherwise
   */
  hasAnyScore(): boolean {
    return this.cachedScore !== null;
  }

  /**
   * Called when a score is saved - clears the cache so it will be refreshed on next call
   * This ensures that the next getLastScore() call will fetch the updated score and count
   * from the API, reflecting the newly saved score.
   */
  onScoreSaved(): void {
    // Clear cache so it will be refreshed on next call with updated count from API
    this.cachedScore = null;
    this.cachedScoreIsWithin6Hours = null;
    this.cachedScoreDate = null;
    this.cachedScoresCountToday = null;
    // Notify subscribers that the cache has been cleared (used by other components)
    this.scoreCacheUpdatedSource.next();
  }

  /**
   * Checks if the score is within a given time period
   * @param score The score to check
   * @param hours The time period to check in hours
   * @returns True if the score is within the time period, false otherwise
   */
  private isScoreWithinTimePeriod(score: LastScoreResponse, hours: number): boolean {
    if (!score) {
      return false;
    }

    // Check for insertLocalDate first (from updated stored procedure), then fall back to other properties for backwards compatibility
    const scoreDate = score.insertLocalDate || (score as any).createdDate || (score as any).insertDate || (score as any).date;

    if (!scoreDate) {
      // If no date property exists, assume score is not within time period
      return false;
    }

    const scoreMoment = moment(scoreDate);
    const now = moment();
    const hoursSinceScore = now.diff(scoreMoment, 'hours', true);
    return hoursSinceScore < hours && hoursSinceScore >= 0;
  }

  acceptButtonClicked() {
    this.updateButtonFlag = true;
    localStorage.removeItem("acceptButtonFlag");
    localStorage.setItem("acceptButtonFlag", JSON.stringify(true));
  }

  resetUpdateButton() {
    this.updateButtonFlag = false;
    localStorage.removeItem("acceptButtonFlag");
    localStorage.setItem("acceptButtonFlag", JSON.stringify(false));
  }

  getUpdateButtonFlag() {
    var flagFromLocalStorage = JSON.parse(
      localStorage.getItem("acceptButtonFlag")
    );
    if (flagFromLocalStorage != null) {
      this.updateButtonFlag = flagFromLocalStorage;
    }
    return this.updateButtonFlag;
  }

  /**
   * Whether the user has already been warned, in this page load, that they are
   * about to score with no Shadows.
   *
   * Deliberately NOT persisted. This flag gates promptForMissingShadows(), which
   * is the only automatic route onto the Shadows tab and therefore the only
   * automatic route to the "use previous shadows?" prompt. Persisting it meant a
   * single look at the Shadows tab (onSectionChange sets this) suppressed the
   * warning for every later visit — and because clearHalosAndZombies() only runs
   * when a score is actually submitted, a `true` left behind by an abandoned
   * session leaked into the next one. Score Now then skipped the warning, never
   * routed to Shadows, never asked about reuse, and submitted an empty Shadows
   * list. Living in memory only, it resets on page load, which is the lifetime
   * the warning actually wants.
   */
  setHasAcknowledgedEmptyShadowsPrompt(): void {
    this.hasAcknowledgedEmptyShadowsPrompt = true;
  }

  getHasAcknowledgedEmptyShadowsPrompt(): boolean {
    return this.hasAcknowledgedEmptyShadowsPrompt;
  }

  /**
   * This function gets the show prompt setting for the given score type.
   * If the setting is not set, it will return true.
   * @param scorePromptType - the type of score to get the prompt for
   * @returns an observable of the load previous scores
   */
  getShowLoadPreviousPrompt(
    scorePromptType: ScoreType
  ): Observable<LoadPreviousScores> {
    const settingName = `LoadPrevious${
      scorePromptType === ScoreType.Halo ? "Halos" : "Zombies"
    }`;

    // check if in local storage first
    const localStorageValue = JSON.parse(localStorage.getItem(settingName));
    if (localStorageValue != null) {
      localStorageValue.newScoreSession = this.getNewScoreSession();
      return of(localStorageValue);
    }

    // if not in local storage, call the API
    return this.settingsLocalService.getUserSetting(settingName).pipe(
      map((result: ResponseTransportOfUserSettingItem) => {
        const response = new LoadPreviousScores();
        response.scoreType = scorePromptType;
        response.newScoreSession = this.getNewScoreSession();
        response.showPrompt = !result.result?.value && response.newScoreSession;
        response.loadPrevious = result.result?.value === "true";
        this.updateLoadPreviousLocalStorage(
          scorePromptType,
          response.loadPrevious,
          response.showPrompt
        );
        return response;
      })
    );
  }

  setNewScoreSession(value: boolean, forceUpdate: boolean = false) {
    // check if exists in local storage
    const localStorageValue = JSON.parse(
      localStorage.getItem("newScoreSession")
    );
    if (localStorageValue != null && !forceUpdate) {
      // A stored value already decided this; adopt it rather than leaving the
      // in-memory copy on whatever it happened to hold (see getNewScoreSession).
      this.newScoreSession = localStorageValue;
      return;
    }
    this.newScoreSession = value;
    localStorage.setItem(
      "newScoreSession",
      JSON.stringify(this.newScoreSession)
    );
    // A write here is the one unambiguous "a session starts now" signal, so it
    // is where the app day gets stamped for expireStaleScoreSessionFlags().
    this.stampScoreSessionDay();
  }

  /**
   * Whether a new scoring session has begun and not yet been submitted. Read
   * through here rather than off the field directly: this service is a root
   * singleton whose `newScoreSession` field defaults to true and is reset on
   * every page load, so without seeding from localStorage the in-memory copy
   * silently disagreed with the stored value after a reload — leaving the
   * reuse prompt showing (or the "always start fresh" clear firing) on a
   * session that had already been initialised, and vice versa.
   */
  getNewScoreSession(): boolean {
    const stored = JSON.parse(localStorage.getItem("newScoreSession"));
    if (stored != null) {
      this.newScoreSession = stored;
    }
    return this.newScoreSession;
  }

  private pendingSuperStar: string | null = null;

  setPendingSuperStar(name: string): void {
    this.pendingSuperStar = name;
  }

  consumePendingSuperStar(): string | null {
    const value = this.pendingSuperStar;
    this.pendingSuperStar = null;
    return value;
  }

  private scoreAgainEntry = false;

  /** Marked when "Score again" (buffer-result) is clicked — it routes to /score, not directly to
   * the session, so this flag lets the session page (stars-and-shadows) know how it was reached
   * once the user clicks through to "Start a new scoring session". */
  markScoreAgainEntry(): void {
    this.scoreAgainEntry = true;
  }

  consumeScoreAgainEntry(): boolean {
    const value = this.scoreAgainEntry;
    this.scoreAgainEntry = false;
    return value;
  }

  getPreLoadHalos() {
    return this.preLoadHalos;
  }

  setPreLoadHalos(value: boolean) {
    this.preLoadHalos = value;
  }

  getPreLoadZombies() {
    return this.preLoadZombies;
  }

  setPreLoadZombies(value: boolean) {
    this.preLoadZombies = value;
  }

  /**
   * FLIP-309: namespaces a draft key by the signed-in account's id, so a second
   * account on the same device can't read the first account's draft. Falls back to
   * the bare key when no user is known (shouldn't happen behind the auth guard, but
   * keeps this safe rather than throwing).
   */
  private draftKey(base: "halos" | "zombies"): string {
    const userId = this.userService.getUserId();
    const key = userId ? `${base}:${userId}` : base;
    this.migrateLegacyDraftKey(base, key);
    return key;
  }

  /**
   * One-time migration for an account with an in-progress draft under the old,
   * unnamespaced key from before FLIP-309 - without this, deploying the namespacing
   * change would silently orphan any draft that was mid-session at deploy time.
   */
  private migrateLegacyDraftKey(legacyKey: string, namespacedKey: string): void {
    if (legacyKey === namespacedKey || localStorage.getItem(namespacedKey) != null) {
      return;
    }
    const legacyValue = localStorage.getItem(legacyKey);
    if (legacyValue != null) {
      localStorage.setItem(namespacedKey, legacyValue);
      localStorage.removeItem(legacyKey);
    }
  }

  /**
   * Writes a draft key, catching a synchronous localStorage failure (quota exceeded, private-mode
   * restrictions, etc.) rather than letting it throw out of a UI event handler. There was no
   * handling for this anywhere in the app before FLIP-302 — grepped for it and found zero
   * try/catch around any localStorage write, Score or Plan.
   *
   * Emits draftWriteStatusChanged(false) on a failure so the caller can show the "couldn't be
   * saved" snackbar, and (true) the moment a write succeeds again after one failed, so the caller
   * can switch it to "Changes saved on this device." A failed write leaves the previous value in
   * place (setItem never partially writes), so the last good draft is never overwritten by a
   * failed one.
   */
  private safeSetItem(key: string, value: string): boolean {
    try {
      localStorage.setItem(key, value);
      if (this.lastDraftWriteFailed) {
        this.lastDraftWriteFailed = false;
        this.draftWriteStatusSource.next(true);
      }
      return true;
    } catch {
      this.lastDraftWriteFailed = true;
      this.draftWriteStatusSource.next(false);
      return false;
    }
  }

  /**
   * Re-attempts the most recent draft write from the in-memory lists — used to retry "before
   * exit" (e.g. the scoring page's ngOnDestroy), since the next in-page change is already covered
   * by every mutator calling safeSetItem() again on its own.
   */
  flushDraftWrite(): boolean {
    const halosOk = this.safeSetItem(this.draftKey("halos"), JSON.stringify(this.halos));
    const zombiesOk = this.safeSetItem(this.draftKey("zombies"), JSON.stringify(this.zombies));
    return halosOk && zombiesOk;
  }

  /**
   * Whether there's an active, unsubmitted draft to offer for recovery — i.e. Score was started
   * at some point (halos and/or zombies non-empty) and never submitted. Used to gate the recovery
   * modal ahead of the reuse-last-submitted-score flow (FLIP-302) — without this, that flow's own
   * logic (both branches of the no-preference prompt, and "always start fresh") clears a still-
   * unsubmitted draft before the user ever sees it.
   *
   * Deliberately NOT gated on halosReuseDecisionMade/zombiesReuseDecisionMade — an earlier version
   * was, on the theory that those flags were a proxy for "already handled this session." They
   * aren't: they stay true for the rest of the browser day once set, so navigating away from a
   * non-empty draft and back *on the same day* silently skipped the recovery modal, which is
   * exactly the "navigated away, then came back" case this is meant to catch.
   *
   * Also deliberately NOT gated on whether a score already exists for today — the app allows up
   * to two scores a day, and a leftover draft from an abandoned second attempt is just as real a
   * draft as a first one. An earlier version suppressed this once any score existed for today,
   * which silently wiped a perfectly legitimate second-score draft.
   */
  hasUnresolvedDraft(): boolean {
    return this.getHalos().length > 0 || this.getZombies().length > 0;
  }

  /**
   * Deletes the draft once Clear all has emptied both Stars and Shadows and the Undo window has
   * expired without Undo being used — otherwise the now-empty draft would linger as a "draft" that
   * hasUnresolvedDraft() never actually flags (it's empty) but that still leaves stale reuse-
   * decision flags around from earlier in the session. No-ops if either list is non-empty, so a
   * Stars-only clear while Shadows still holds items never wipes the still-real other half.
   */
  deleteDraftIfEmpty(): void {
    if (this.getHalos().length === 0 && this.getZombies().length === 0) {
      this.clearHalosAndZombies();
    }
  }

  updateHalo(halos: ScoreItem[], item: ScoreItem, index: number) {
    this.halos = halos;
    this.halos[index] = item;
    this.safeSetItem(this.draftKey("halos"), JSON.stringify(this.halos));
  }

  replaceHalo(index: number, item: ScoreItem) {
    this.halos[index] = item;
    this.safeSetItem(this.draftKey("halos"), JSON.stringify(this.halos));
    this.halosUpdated();
  }

  addHalo(item: ScoreItem) {
    this.halos.push(item);
    this.safeSetItem(this.draftKey("halos"), JSON.stringify(this.halos));
    this.halosUpdated();
  }

  removeHalo(index: number) {
    this.halos.splice(index, 1);
    this.safeSetItem(this.draftKey("halos"), JSON.stringify(this.halos));
    this.halosUpdated();
  }

  removeAllHalo() {
    while (this.halos.length != 0) {
      this.halos.pop();
    }
    this.safeSetItem(this.draftKey("halos"), JSON.stringify(this.halos));
    this.halosUpdated();
  }

  halosUpdated() {
    this.haloSource.next();
  }

  getHalos() {
    var halosFromLocalStorage = JSON.parse(localStorage.getItem(this.draftKey("halos")));
    if (halosFromLocalStorage != null) {
      this.halos = halosFromLocalStorage;
    }
    //the belwo else case is to remove persistent data on chnaging logins in the same instance of the app.
    else {
      this.halos = [];
    }
    return this.halos;
  }

  getTotalHalos() {
    if (this.halos.length > 0) {
      return this.halos
        .map((a) => a.stars)
        .reduce((partial_sum, a) => partial_sum + a);
    } else {
      return 0;
    }
  }

  updateZombie(zombies: ScoreItem[], item: ScoreItem, index: number) {
    this.zombies = zombies;
    this.zombies[index] = item;
    this.safeSetItem(this.draftKey("zombies"), JSON.stringify(this.zombies));
  }

  replaceZombie(index: number, item: ScoreItem) {
    this.zombies[index] = item;
    this.safeSetItem(this.draftKey("zombies"), JSON.stringify(this.zombies));
    this.zombiesUpdated();
  }

  addZombie(item: ScoreItem) {
    this.zombies.push(item);
    this.safeSetItem(this.draftKey("zombies"), JSON.stringify(this.zombies));
    this.zombiesUpdated();
  }

  removeZombie(index: number) {
    this.zombies.splice(index, 1);
    this.safeSetItem(this.draftKey("zombies"), JSON.stringify(this.zombies));
    this.zombiesUpdated();
  }

  removeAllZombie() {
    while (this.zombies.length != 0) {
      this.zombies.pop();
    }
    this.safeSetItem(this.draftKey("zombies"), JSON.stringify(this.zombies));
    this.zombiesUpdated();
  }

  zombiesUpdated() {
    this.zombieSource.next();
  }

  getZombies() {
    var zombiesFromLocalStorage = JSON.parse(localStorage.getItem(this.draftKey("zombies")));
    if (zombiesFromLocalStorage != null) {
      this.zombies = zombiesFromLocalStorage;
    }
    return this.zombies;
  }

  getTotalZombies() {
    if (this.zombies.length > 0) {
      return this.zombies
        .map((a) => a.stars)
        .reduce((partial_sum, a) => partial_sum + a);
    } else {
      return 0;
    }
  }

  /**
   * @returns Whether the halos/zombies draft was actually removed. Callers that must not proceed
   * on a failed delete (e.g. Start fresh — FLIP-302) should check this rather than assuming
   * success; a failure here leaves the in-memory and stored draft untouched rather than desyncing
   * them from a partial clear.
   */
  clearHalosAndZombies(): boolean {
    try {
      localStorage.removeItem(this.draftKey("halos"));
      localStorage.removeItem(this.draftKey("zombies"));
    } catch {
      this.draftWriteStatusSource.next(false);
      return false;
    }
    // No longer written (the flag is in-memory now); removed here so the key
    // left behind by an earlier build doesn't linger in existing users' storage.
    localStorage.removeItem("hasAcknowledgedEmptyShadowsPrompt");
    localStorage.removeItem("halosReuseDecisionMade");
    localStorage.removeItem("zombiesReuseDecisionMade");
    // getShowLoadPreviousPrompt() caches its computed decision (including
    // showPrompt, which factors in newScoreSession at the time it was computed)
    // under these keys to avoid re-asking within a session. That cache has no
    // other expiry, so without clearing it here it silently outlives the
    // session it was computed for and overrides the real DB preference on
    // every future session — e.g. a decision cached while newScoreSession was
    // false permanently suppresses "always ask" even once a genuinely new
    // session begins and newScoreSession is true again.
    localStorage.removeItem("LoadPreviousHalos");
    localStorage.removeItem("LoadPreviousZombies");
    this.halos = [];
    this.zombies = [];
    this.hasAcknowledgedEmptyShadowsPrompt = false;
    return true;
  }

  /**
   * Whether the Stars reuse-previous decision (prompt answered, or "always
   * load"/"always fresh" applied) has already been made for the current
   * score session. Separate from newScoreSession, which is shared with
   * Shadows — flipping that after the Stars decision would starve Shadows
   * of its own first-preload signal when the user later switches tabs.
   * Reset only by clearHalosAndZombies() (i.e. a genuinely new session).
   * Settings intentionally does NOT clear this on a mid-session preference
   * change — doing so used to make an in-progress, unsubmitted score look
   * like a fresh session on the next visit to Score, wiping it. A changed
   * preference still takes effect, just starting with the next session.
   */
  getHalosReuseDecisionMade(): boolean {
    return JSON.parse(localStorage.getItem("halosReuseDecisionMade")) === true;
  }

  setHalosReuseDecisionMade(): void {
    localStorage.setItem("halosReuseDecisionMade", JSON.stringify(true));
  }

  /**
   * Shadows counterpart of getHalosReuseDecisionMade(). Shadows previously
   * signalled "already handled" by setting the *shared* newScoreSession to
   * false, which pinned it false for the rest of the session and thereby
   * suppressed the Stars prompt and the Stars "always start fresh" clear
   * (both gated on newScoreSession) on every subsequent visit. Recording the
   * decision per type keeps the two sides independent.
   */
  getZombiesReuseDecisionMade(): boolean {
    return JSON.parse(localStorage.getItem("zombiesReuseDecisionMade")) === true;
  }

  setZombiesReuseDecisionMade(): void {
    localStorage.setItem("zombiesReuseDecisionMade", JSON.stringify(true));
  }

  /** Synchronous read of the current categories cache — no API call. */
  getCachedCategories(): CategoryDto[] {
    return this.categoriesSource.value;
  }

  getCategories() {
    // Cancel any still-in-flight fetch from a previous session so a slow, stale
    // response can't land after this one and overwrite it with the wrong user's data.
    this.cancelCategoriesFetch$.next();

    this.scoreService
      .getCategories()
      .pipe(
        take(1),
        takeUntil(this.cancelCategoriesFetch$),
        tap((result) => {
          this.categoriesSource.next(result);
        })
      )
      .subscribe({
        // Swallow errors: a failed categories fetch must not surface as an unhandled
        // rejection. Consumers (add-score/-tag/-star-shadow dialogs) read the seeded
        // BehaviorSubject and simply get an empty list until a later successful fetch.
        error: () => {},
      });
  }

  /**
   * Resets the categories/tags cache on logout. ScoreLocalService is a root
   * singleton, so without this a second user logging into the same browser
   * tab would otherwise see the previous user's cached custom tags until a
   * fresh fetch happened to land.
   */
  clearCategories() {
    this.cancelCategoriesFetch$.next();
    this.categoriesSource.next([]);
  }

  openScoreDialog(item: ScoreItem = null) {
    this.scoreDialogRequestedSource.next(item);
  }

  /**
   *
   * @param scoreType - the type of score to update the load previous for
   * @param loadPrevious - whether to load previous scores
   * @param showPrompt - whether to show the prompt
   * @param newScoreSession - whether to set the new score session
   */
  updateLoadPreviousLocalStorage(
    scoreType: ScoreType,
    loadPrevious?: boolean,
    showPrompt?: boolean
  ) {
    const settingName = `LoadPrevious${
      scoreType === ScoreType.Halo ? "Halos" : "Zombies"
    }`;
    let localStorageValue = JSON.parse(localStorage.getItem(settingName));
    // if not in local storage, create a new one
    if (localStorageValue == null) {
      localStorageValue = new LoadPreviousScores();
      localStorageValue.scoreType = scoreType;
      localStorageValue.loadPrevious = loadPrevious;
      localStorageValue.showPrompt = showPrompt;
    } else {
      // if in local storage, update the values
      if (loadPrevious != null) {
        localStorageValue.loadPrevious = loadPrevious;
      }
      if (showPrompt != null) {
        localStorageValue.showPrompt = showPrompt;
      }
    }
    localStorage.setItem(settingName, JSON.stringify(localStorageValue));
  }
}
